import { Assignment } from '@mui/icons-material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'
import FlightOutlinedIcon from '@mui/icons-material/FlightOutlined'
import ShareLocationIcon from '@mui/icons-material/ShareLocation'
import {
  Avatar,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { JourneyTypeLabels } from 'api/models/Common/JyrneyEnums'
import isSelectedService from 'components/Bookings/Common/isSelectedService'
import DisplayPrice from 'components/Common/DisplayValues/DisplayPrice'
import StatusChip from 'components/UI/StatusChip'
import { getSafeColorsForName } from 'helpers/avatarColours'

const normaliseFlightNumber = (value: any) => {
  const flightNumber = String(value ?? '').trim()
  if (!flightNumber || flightNumber.toUpperCase() === 'N/A') return ''
  return flightNumber
}

const findNestedFlightNumber = (root: any) => {
  if (!root || typeof root !== 'object') return ''

  const visited = new Set<any>()
  const queue = [root]

  while (queue.length > 0) {
    const node = queue.shift()
    if (!node || typeof node !== 'object' || visited.has(node)) continue
    visited.add(node)

    if (!Array.isArray(node) && 'flightNumber' in node) {
      const flightNumber = normaliseFlightNumber((node as any).flightNumber)
      if (flightNumber) return flightNumber
    }

    if (Array.isArray(node)) {
      node.forEach((item) => queue.push(item))
      continue
    }

    Object.values(node).forEach((value) => queue.push(value))
  }

  return ''
}

const BookingInfo = ({ currentBooking, preparedQuote }: any) => {
  const { booker, onBehalfOf } = currentBooking.booking

  const { background: behalfBackground, text: behalfText } =
    getSafeColorsForName(`${onBehalfOf.firstName}${onBehalfOf.lastName}`)
  getSafeColorsForName(`${onBehalfOf.firstName}${onBehalfOf.lastName}`)
  const { background: bookerBackground, text: bookerText } =
    getSafeColorsForName(`${booker.firstName}${booker.lastName}`)

  getSafeColorsForName(`${booker.firstName}${booker.lastName}`)

  function sumPrices(jobs: any[]) {
    let usedActualPrice = false
    const summedJobs = jobs.reduce(
      (acc, job) => {
        if (job?.actualPrice) {
          usedActualPrice = true
        }
        const jp = job?.actualPrice ?? job?.estimatedPrice ?? {}
        acc.total += jp.total ?? 0
        acc.price += jp.price ?? 0
        acc.vat += jp.vat ?? 0
        acc.currency = acc.currency || jp.currency || 'GBP'
        return acc
      },
      { total: 0, price: 0, vat: 0, currency: '' },
    )
    return { summedJobs, usedActualPrice }
  }

  const { summedJobs, usedActualPrice } = sumPrices(currentBooking?.jobs ?? [])
  const showMeetAndGreetIcon = (currentBooking?.jobs ?? []).some((job: any) =>
    isSelectedService({
      services: job?.services ?? [],
      selectedService: 'MeetAndGreet',
    }),
  )
  const showDestinationSupplierIcon = (currentBooking?.jobs ?? []).some(
    (job: any) => job?.supplier?.isDestinationLogicSupplier === true,
  )
  const showFlexibleBookingIcon =
    currentBooking?.booking?.journeyTypeName === 'Flexible' ||
    (currentBooking?.jobs ?? []).some(
      (job: any) => job?.journeyTypeName === 'Flexible',
    )
  const showImmediateJourneyIcon =
    currentBooking?.booking?.isImmediateJourney === true ||
    (currentBooking?.jobs ?? []).some(
      (job: any) => job?.isImmediateJourney === true,
    )
  const flightNumber =
    normaliseFlightNumber(
      currentBooking?.booking?.flightDetails?.flightNumber,
    ) ||
    normaliseFlightNumber(currentBooking?.booking?.flightNumber) ||
    normaliseFlightNumber(currentBooking?.flightDetails?.flightNumber) ||
    normaliseFlightNumber(currentBooking?.flightNumber) ||
    findNestedFlightNumber(currentBooking) ||
    normaliseFlightNumber(
      preparedQuote?.outbound?.flightDetails?.flightNumber,
    ) ||
    findNestedFlightNumber(preparedQuote)
  const showFlightNumberIcon = flightNumber !== ''
  const bookingTypeLabel =
    Object.entries(JourneyTypeLabels).find(
      ([key]) => key === currentBooking?.booking?.journeyTypeName,
    )?.[1] ?? ''

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          gap: '15px',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            height: 'auto',
          }}
        >
          Booking Information
        </Typography>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 300 }} size="small" aria-label="a dense table">
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">
                  {`Booking Total: (${usedActualPrice ? 'Actual' : 'Est.'})`}
                </TableCell>
                <TableCell align="right">
                  {DisplayPrice({
                    VATTotalPrice: summedJobs,
                    displayVATPrice: currentBooking?.booking.client.displayVAT,
                    justify: 'flex-end',
                  })}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Booking Status:
                </TableCell>
                <TableCell align="right">
                  <StatusChip
                    status={currentBooking?.booking.bookingStatusTypeName}
                    statusType={'BookingStatus'}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Booking Type:
                </TableCell>
                <TableCell align="right">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '6px',
                    }}
                  >
                    <Box component="span">{bookingTypeLabel}</Box>
                    {showMeetAndGreetIcon && (
                      <Tooltip title="Meet & Greet">
                        <Assignment
                          fontSize="small"
                          sx={(theme) => ({
                            color: theme.palette.info.dark,
                            verticalAlign: 'middle',
                          })}
                        />
                      </Tooltip>
                    )}
                    {showDestinationSupplierIcon && (
                      <Tooltip title="Destination Supplier">
                        <ShareLocationIcon
                          fontSize="small"
                          sx={(theme) => ({
                            color: theme.palette.info.dark,
                            verticalAlign: 'middle',
                          })}
                        />
                      </Tooltip>
                    )}
                    {showFlexibleBookingIcon && (
                      <Tooltip title="Flexible Booking">
                        <AccessTimeIcon
                          fontSize="small"
                          sx={(theme) => ({
                            color: theme.palette.info.dark,
                            verticalAlign: 'middle',
                          })}
                        />
                      </Tooltip>
                    )}
                    {showImmediateJourneyIcon && (
                      <Tooltip title="Immediate Journey">
                        <EmojiPeopleIcon
                          fontSize="small"
                          sx={(theme) => ({
                            color: theme.palette.info.dark,
                            verticalAlign: 'middle',
                          })}
                        />
                      </Tooltip>
                    )}
                    {showFlightNumberIcon && (
                      <Tooltip title={`Flight: ${flightNumber}`}>
                        <FlightOutlinedIcon
                          fontSize="small"
                          sx={(theme) => ({
                            color: theme.palette.info.dark,
                            verticalAlign: 'middle',
                          })}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Booked By:
                </TableCell>
                <TableCell align="right">
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '10px',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: bookerBackground,
                        text: bookerText,
                        width: 24,
                        height: 24,
                        fontSize: '14px',
                      }}
                    >
                      {booker.firstName && booker.lastName
                        ? `${booker.firstName.charAt(
                            0,
                          )}${booker.lastName.charAt(0)}`
                        : 'API'}
                    </Avatar>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: '10px',
                      }}
                    >
                      <Typography>
                        {booker.firstName && booker.lastName
                          ? `${booker.firstName} ${booker.lastName}`
                          : 'API Manager'}
                      </Typography>
                      <Typography>
                        <a
                          href={`tel:${booker.contactNumber}`}
                          style={{
                            textDecoration: 'none',
                            color: 'inherit',
                          }}
                        >
                          {booker.contactNumber}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
              {onBehalfOf.id !== 0 ? (
                <TableRow>
                  <TableCell component="th" scope="row">
                    On Behalf of:
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '10px',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: behalfBackground,
                          text: behalfText,
                          width: 24,
                          height: 24,
                          fontSize: '14px',
                        }}
                      >
                        {`${onBehalfOf.firstName.charAt(
                          0,
                        )}${onBehalfOf.lastName.charAt(0)}`}
                      </Avatar>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          gap: '10px',
                        }}
                      >
                        <Typography>{`${onBehalfOf.firstName} ${onBehalfOf.lastName}`}</Typography>
                        <Typography>
                          <a
                            href={`tel:${onBehalfOf.contactNumber}`}
                            style={{
                              textDecoration: 'none',
                              color: 'inherit',
                            }}
                          >
                            {onBehalfOf.contactNumber}
                          </a>
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <></>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  )
}

export default BookingInfo
